// The state of one promo code field at checkout. The code lives in this hook's state only:
// never in the URL, in storage, in a query key or in an analytics event.
import { useCallback, useEffect, useRef, useState } from 'react'
import { apiV2 } from '@/lib/api-v2-client'
import type { BillingInterval, PaidPlan } from '@/lib/plans'
import { classifyPromoError, PROMO_COPY, rateLimitedText, toPromoInterval, type PromoTerms } from '@/lib/promo'

/** The wait between leaving the field and the request. Enter does not wait. */
export const PROMO_BLUR_DELAY_MS = 200

export type PromoState =
  | { phase: 'idle' }
  | { phase: 'checking' }
  | { phase: 'valid'; terms: PromoTerms; plan: PaidPlan; interval: BillingInterval }
  | { phase: 'invalid' | 'limited' | 'error'; message: string }

export type PromoChoice = { ok: true; code?: string } | { ok: false }

export function usePromoCode(plan: PaidPlan, interval: BillingInterval) {
  const [code, setCodeState] = useState('')
  const [state, setState] = useState<PromoState>({ phase: 'idle' })
  const codeRef = useRef('')
  const requestId = useRef(0)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inflight = useRef<{ key: string; promise: Promise<boolean> } | null>(null)
  // The last final answer (valid or invalid) per code, plan and interval: no second request for the same question.
  const settled = useRef<{ key: string; ok: boolean } | null>(null)

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
  }

  /** Ask the server. True when the code is valid for `forPlan` and `forInterval`. */
  const check = useCallback((forPlan: PaidPlan, forInterval: BillingInterval): Promise<boolean> => {
    const value = codeRef.current
    const key = `${value}\n${forPlan}\n${forInterval}`
    if (settled.current?.key === key) return Promise.resolve(settled.current.ok)
    if (inflight.current?.key === key) return inflight.current.promise
    settled.current = null
    const id = ++requestId.current
    setState({ phase: 'checking' })
    const promise = apiV2
      .validatePromo({ code: value, plan: forPlan, interval: toPromoInterval(forInterval) })
      .then((answer) => {
        if (id !== requestId.current) return false
        settled.current = { key, ok: true }
        setState({
          phase: 'valid',
          terms: { percent_off: answer.percent_off, duration_intervals: answer.duration_intervals },
          plan: forPlan,
          interval: forInterval,
        })
        return true
      })
      .catch((err: unknown) => {
        if (id !== requestId.current) return false
        const e = classifyPromoError(err)
        if (e.kind === 'invalid') {
          settled.current = { key, ok: false }
          setState({ phase: 'invalid', message: PROMO_COPY.invalid })
        } else if (e.kind === 'rate_limited') {
          setState({ phase: 'limited', message: rateLimitedText(e.retryAfterSecs) })
        } else {
          setState({ phase: 'error', message: PROMO_COPY.failed })
        }
        return false
      })
      .finally(() => {
        if (inflight.current?.key === key) inflight.current = null
      })
    inflight.current = { key, promise }
    return promise
  }, [])

  const setCode = useCallback((value: string) => {
    codeRef.current = value
    setCodeState(value)
    requestId.current += 1
    inflight.current = null
    settled.current = null
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    setState({ phase: 'idle' })
  }, [])

  /** Enter: ask now. */
  const submit = useCallback(() => {
    clearTimer()
    if (codeRef.current.trim() !== '') void check(plan, interval)
  }, [check, plan, interval])

  /** Blur: ask after a short delay. */
  const blur = useCallback(() => {
    clearTimer()
    if (codeRef.current.trim() === '') return
    timer.current = setTimeout(() => {
      timer.current = null
      void check(plan, interval)
    }, PROMO_BLUR_DELAY_MS)
  }, [check, plan, interval])

  // A new interval changes the question: ask again for a code that was already answered.
  const lastInterval = useRef(interval)
  useEffect(() => {
    if (lastInterval.current === interval) return
    lastInterval.current = interval
    if (codeRef.current.trim() !== '' && settled.current) void check(plan, interval)
  }, [interval, plan, check])

  useEffect(() => clearTimer, [])

  /**
   * At the buy button: the code to send for `forPlan`. With no code, send nothing. With a code, ask first
   * and send it only after a valid answer; on any other answer the checkout must not start.
   */
  const choose = useCallback(
    async (forPlan: PaidPlan): Promise<PromoChoice> => {
      clearTimer()
      const value = codeRef.current
      if (value.trim() === '') return { ok: true }
      return (await check(forPlan, interval)) ? { ok: true, code: value } : { ok: false }
    },
    [check, interval],
  )

  /** A 422 from checkout itself: the code is no longer valid. */
  const markInvalid = useCallback(() => {
    settled.current = null
    setState({ phase: 'invalid', message: PROMO_COPY.invalid })
  }, [])

  return { code, setCode, state, submit, blur, choose, markInvalid, busy: state.phase === 'checking' }
}
